import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import * as Sentry from '@sentry/angular';
import { IUserLoginRequest } from "../models/IUserLoginRequest";
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, firstValueFrom, from, Observable, of, Subscription} from "rxjs";
import { JwtHelperService } from "@auth0/angular-jwt";
import { IUserToken } from "../models/IUserToken";
import { environment } from "../../../../../environments/environment";
import { NewPasswordModel } from "../models/new-password.model";
import { SendRecoveryPasswordMailRequest } from "../models/SendRecoveryPasswordMailRequest.model";
import { AuthAPIService } from "../../../shared/services/API/AuthAPI.service";
import { LocalStorageService } from "../../../shared/services/LocalStorage.service";
import { SignalRService } from "../../../shared/services/Signal-R.service";
import { ImageCacheService } from "../../../shared/services/ImageCache.service";

@Injectable({
  providedIn: "root",
})
export class AuthService implements OnInit, OnDestroy {
  private userId: string = "";
  public isAuthenticated: boolean = false;
  public isReloadRequired: boolean = false;
  public isReloading = false;
  formLogin: any;
  configUrl: string;

  requestUrl: any;
  // requestToken: any;
  public queryParams: { [key: string]: string } = { };

  hasTokenError: boolean = false;
  needsCallback: boolean = false;

  
  subscription_GetRefreshToken: Subscription;
  subscription_CheckAccess: Subscription;
  public roleItem$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  subscription_SetClients: any;


  constructor(
    private router: Router,
    private jwtHelper: JwtHelperService,
    private authService: AuthAPIService,
    private localStorageService: LocalStorageService,
    private imageCache: ImageCacheService,
  ) {
    this.configUrl = environment.host;
  }

  ngOnInit() {
  }

  ngOnDestroy(): void {
    if (this.subscription_CheckAccess)
      this.subscription_CheckAccess.unsubscribe();
    if (this.subscription_GetRefreshToken)
      this.subscription_GetRefreshToken.unsubscribe();
  }

  setRole(resp) {
    this.roleItem$.next(resp);
  }
  
  getRoleObservable(): Observable<string> {
    return this.roleItem$.asObservable();
  }

  getParamsFromUrl() {

    // this.localStorageService.clearRedirectUrl();
    const urlSearchParams = new URLSearchParams(window.location.href.split("?")[1]);
    const params = Object.fromEntries(urlSearchParams.entries());
    this.queryParams = { ...this.queryParams, ...params };
    if ('token' in this.queryParams) {
      const requestToken = this.queryParams["token"];
      delete this.queryParams["token"];

      if (requestToken && requestToken !== "") {
        if(!environment.production) console.log("----Seteando Token por URL----");

        const currentToken = this.localStorageService.getToken();
        if (currentToken != requestToken) {
          this.isReloadRequired = true;
        }

        this.localStorageService.setToken(requestToken);
        this.localStorageService.setUserName(this.getUserDecoded(requestToken));

        //si hay un path especifico con token, se guarda para redireccionar despues de login
        const currentPath = window.location.hash ? window.location.hash.replace('#', '').split('?')[0] : window.location.pathname;
        if(!environment.production) console.log("currenr path", currentPath);
        
        const ignoredPaths = ['/callback', '/login', '/error', '/'];
        if (currentPath && !ignoredPaths.includes(currentPath)) {
          this.localStorageService.setRedirectUrl(currentPath);
        }
      }
    }

    const beforeParams = JSON.parse(this.localStorageService.getQueryParams() || "{}");
    const paramsTotal = Object.keys(this.queryParams).length;
    
    if(!environment.production) console.log("reload params 1", beforeParams, this.queryParams, paramsTotal);
    if(!environment.production) console.log("reload params 2", this.localStorageService.getIsReAuth() !== "true" || (paramsTotal !== 0 && beforeParams !== this.queryParams));

    if (this.localStorageService.getIsReAuth() !== "true" || (paramsTotal !== 0 && beforeParams !== this.queryParams)) {    
      this.isReloadRequiredByParamChange(beforeParams, this.queryParams);
      this.localStorageService.setQueryParams(JSON.stringify(this.queryParams));
    }
    if(!environment.production) console.log("reload params 3", this.localStorageService.getQueryParams(), this.isReloadRequired);
  }

  isReloadRequiredByParamChange(beforeParams: { [key: string]: string }, afterParams: { [key: string]: string }) {
    var keys = ['mapBuild', 'lat', 'lng', 'destinationLat', 'destinationLng'];

    this.isReloadRequired = keys.some(key => 
      beforeParams[key] != null && 
      afterParams[key] != null && 
      beforeParams[key] !== afterParams[key]
    );
  }

  //hice asincrono la verificaciond el token, así que para los que usan el subscribe se ocupa transformar el promise a observable
  checkAuthenticated(): Observable<boolean> {
    return from(this.checkAuthenticatedAsync());
  }

  private async checkAuthenticatedAsync(): Promise<boolean> {
    this.hasTokenError = false;
    if(!environment.production) console.log("Checando Token...");
    this.getParamsFromUrl();
    
    const currentToken = this.localStorageService.getToken();

    //checa si hay token
    if(!currentToken || currentToken === "null" || currentToken === "") {
      if(!environment.production) console.log("----Token no proporcionado----");
      this.isAuthenticated = false;
      this.localStorageService.setIsReAuth("");
      await this.Logout();
      return this.isAuthenticated;
    }
    
    //checa si el token se puede decodificar
    const userName = this.getUserDecoded(currentToken);
    if (!userName || userName === "") {
      if(!environment.production) console.log("----Token no válido (no se puede decodificar)----"); 
      this.hasTokenError = true;
      this.isAuthenticated = false;
      this.router.navigate(["/error"]);
      return this.isAuthenticated;
    }
    
    //y checa expiracion y refresco
    const verifiedToken = await this.verifyTokenExpiration(currentToken);

    if (verifiedToken && verifiedToken !== "") {
      this.localStorageService.setToken(verifiedToken);
      this.localStorageService.setUserName(this.getUserDecoded(verifiedToken));
      this.isAuthenticated = true;

      if (this.localStorageService.getIsReAuth() !== "true"){
        this.localStorageService.setIsReAuth("true");
        this.needsCallback = true;
      }else if (this.isReloadRequired){
        this.isReloadRequired = false;
        this.needsCallback = true;
      }else{
        this.needsCallback = false;
      }
    }else{
      if(!environment.production) console.log("----Token expirado y no se pudo refrescar----");
      this.isAuthenticated = false;
      this.localStorageService.setIsReAuth("");
      await this.Logout();
    }

    return this.isAuthenticated;
  }

  async verifyTokenExpiration(token: string): Promise<string> {
    try {
      var isTokenExpired = this.jwtHelper.isTokenExpired(token);
    } catch (error) {
      isTokenExpired = true;
    }

    if (isTokenExpired) {
      if(!environment.production) console.log("**********TOKEN EXPIRED*******");

      try {
        const TOKEN: IUserToken = { token: token };
        const resp = await firstValueFrom(this.getRefreshToken(TOKEN));
        
        if (resp.result != null && resp.result.token) {
          return resp.result.token;
        } else {
          Sentry.captureMessage('AuthService.GetToken: refreshToken result es nulo', { level: 'warning' });
          return "";
        }
      } catch (error) {
        console.error('Error al obtener token de refresco', error);
        return "";
      }

      // if (this.requestToken !== undefined ) {
      //   return "";
      // } else {
      //   const TOKEN: IUserToken = { token: token };
      //   this.subscription_GetRefreshToken = this.getRefreshToken( TOKEN ).subscribe({
      //     next: (resp) => {
      //       if (resp.result != null){
      //         var userToken: IUserToken;
      //       }
      //       else {
      //         Sentry.captureMessage('AuthService.GetToken: refreshToken result es nulo', { level: 'warning' });
      //       }
      //       userToken = resp.result;
      //       return userToken.token;
      //     },
      //     error: (error) => {
      //       // TODO marca para complete despues
      //       console.error('Error al obtener token de refresco', error);
      //     }
      //   });
      // }
    } else {
      if(!environment.production) console.log("*******SESSION TOKEN*******************");
      return token;
    }
  }

  getUserDecoded(requestToken: any): string {
    var decode = this.getDecodedAccessToken(requestToken);
    this.userId = decode?.Id || "";
    if (decode == null)
      return "";
    return decode.email;
  }

  getDecodedAccessToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch(Error) {
      return null;
    }
  }
  getRefreshToken(token: any) {
    return this.authService.getRefreshToken(token);
  }

  Login(usuarioLogin: IUserLoginRequest): any {
    return this.authService.Login(usuarioLogin);
  }

  async Logout(): Promise<void> {
    try {
      if(!environment.production) console.log("Cerrando Sesion");
      this.clearStorage();
      await this.router.navigate(["/login"]);
      //location.reload();
    } catch (err) {
      console.error(err);
    }
  }

  clearStorage() {
    try {
      if(!environment.production) console.log("Cerrando Sesion");
      this.localStorageService.setToken("");
      this.localStorageService.setBloqueoGPS("");
      this.localStorageService.setDesbloqueoGPS("");
      this.localStorageService.setUser("");
      this.localStorageService.setUserName("");
      this.localStorageService.setUserRol("");
      this.localStorageService.setListaClientes("");
      this.localStorageService.setClienteSeleccionado("");
      this.localStorageService.setClienteSeleccionadoId("");
      this.localStorageService.setViews("");
      this.localStorageService.setUrlList("");
      this.localStorageService.setListaCategorias("");
      this.localStorageService.setPermissions("");
      this.localStorageService.setShownTutorials("");
      this.localStorageService.setSystemNotification("");
      this.localStorageService.setSettingsSelectedTab("");
      this.localStorageService.setQueryParams("");
      this.queryParams = { };
      this.isAuthenticated = false;
      this.localStorageService.setIsReAuth("");
      this.localStorageService.setSelectedBuildName("");
      this.localStorageService.setMapLoadedViews("[]");
      this.localStorageService.clearRedirectUrl();
      this.imageCache.clearImageCache();
      //location.reload();
    } catch (err) {
      console.error(err);
    }
  }

  reloadCurrentRoute() {
    const currentUrl = this.router.url;
    this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  getUserId(): string {
    return this.userId;
  }

  resetPassword(objResetPassword: NewPasswordModel){
    return this.authService.resetPassword(objResetPassword);
  }
    
  sendRecoveryPasswordEmail(objSendPasswordRecoveryMail: SendRecoveryPasswordMailRequest){
    return this.authService.sendRecoveryPasswordEmail(objSendPasswordRecoveryMail);
  }

  UpdateDefaultClientId() {
    return this.authService.UpdateDefaultClientId();
  }

  UpdateClientId(client: any) {
    return this.authService.UpdateClientId(client);
  }
}
